import { DEFAULT_LOCALE, isLiveLocale, type LiveLocale } from "./locales.ts";

/**
 * Picks the reader's language for the app, mail and server copy (2.0's UserLocale, kept):
 * 1. the language the person chose on their profile,
 * 2. else the browser's Accept-Language,
 * 3. else the company's default,
 * 4. else English.
 * Only live languages are ever returned.
 */
export function resolveLocale(input: {
  chosen?: string | null | undefined;
  acceptLanguage?: string | null | undefined;
  company?: string | null | undefined;
}): LiveLocale {
  if (isLiveLocale(input.chosen)) return input.chosen;
  const fromHeader = localeFromAcceptLanguage(input.acceptLanguage);
  if (fromHeader) return fromHeader;
  if (isLiveLocale(input.company)) return input.company;
  return DEFAULT_LOCALE;
}

/** The first live language in an Accept-Language header, by quality. `da-DK` and `da` both mean Danish. */
export function localeFromAcceptLanguage(header: string | null | undefined): LiveLocale | null {
  if (!header) return null;
  const ranked = header
    .split(",")
    .map((part, index) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const quality = q ? Number.parseFloat(q.slice(2)) : 1;
      return {
        base: tag.toLowerCase().split("-")[0] ?? "",
        quality: Number.isFinite(quality) ? quality : 0,
        index,
      };
    })
    .filter((entry) => entry.base && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index);
  for (const entry of ranked) {
    if (isLiveLocale(entry.base)) return entry.base;
  }
  return null;
}
